
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { KaziModal } from '../../../../Shared Components/Kazi/KaziModal';
import { Button, Grid, TextField } from '@mui/material';
import { KaziTextFieldWithLabel } from '../../../../Shared Components/Kazi/KaziTextFieldWithLabel';
import { Controller, useForm } from 'react-hook-form';
import { KaziIndustries } from '../../../../Shared Components/Kazi/KaziIndustries';
import { KaziCountries } from '../../../../Shared Components/Kazi/KaziCountries';
import { yupResolver } from '@hookform/resolvers/yup';
import { EnterpriseUpgradeModalSchema } from '../../../../Schemas/SubscriptionsSchemas';
import { KaziPhoneNumber } from '../../../../Shared Components/Kazi/KaziPhoneNumber';
import { IEnterpriseUpgradeModalForm } from '../../../../Types/subcription';
import { useUser } from '../../../../Hooks/useUser';
import { useEnterpriseRequestMutation } from '../../../../Redux/api/subscriptionApi';
import { useToast } from '../../../../Hooks/useToast';
import { KaziButton } from '../../../../Shared Components/Kazi/KaziButton';

interface EnterpriseUpgradeModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
}
export const EnterpriseUpgradeModal = ({open, setOpen}:EnterpriseUpgradeModalProps ) => {
    /* ******************** Hooks ******************** */
    const { user } = useUser();
    const { showToast } = useToast();
    const [enterpriseRequest, { isLoading: requestLoading }] = useEnterpriseRequestMutation();
    const {
        control,
        formState: {isValid, errors},
        getValues,
        handleSubmit, 
        watch,
        reset
    } = useForm<IEnterpriseUpgradeModalForm>({
        defaultValues: {
            FirstName: user?.first_name ?? '',
            LastName: user?. last_name ?? '',
            CompanyName: user?.company ?? '',
            PhoneNumber: user?.phone ?? '',
            EmailAddress: user?. email ?? '',
            Website: '',
            Industry: '',
            Country: user?.country ?? '',
            Description: ''
        },
        resolver: yupResolver(EnterpriseUpgradeModalSchema),
        mode: "onChange",
        reValidateMode: "onChange" 
    })
    /* ******************** Variables ******************** */
    const country = watch('Country')
    /* ******************** Functions ******************** */
    const handleClose = () => {
        setOpen(false)
        reset();
    }
    const onSubmit = (data: IEnterpriseUpgradeModalForm) => {
        enterpriseRequest(data)
        .unwrap()
        .then(() => {
            handleClose();
            showToast({
                text: "Request sent"
            })
        })
        .catch( err => {
            console.error("Error occured while making request for enterprise upgrade: ", err)
            showToast({
                text: "Something went wrong",
                variant: "error"
            })
        })
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <KaziModal
            open={open}
            handleClose={handleClose}
            title='Contact us for a free quote'
            content={
                <>
                    <Grid display={'flex'} flexDirection={'row'} gap={2}>
                        <Controller 
                            name="FirstName"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    error={errors.FirstName != undefined}
                                    fieldLabel='First name'
                                />
                            )}
                        />
                        <Controller 
                            name="LastName"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    error={errors.LastName != undefined}
                                    fieldLabel='Last name'
                                />
                            )}
                        />
                    </Grid>
                    <Grid display={'flex'} flexDirection={'row'} gap={2}>
                        <Controller 
                            name="CompanyName"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    error={errors.CompanyName != undefined}
                                    fieldLabel='Company name'
                                />
                            )}
                        />
                        <Controller 
                            name="PhoneNumber"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziPhoneNumber 
                                    {...field}
                                    error={errors.PhoneNumber != undefined}
                                    format={country}
                                    showLabel
                                />
                            )}
                        />
                    </Grid>
                    <Grid display={'flex'} flexDirection={'row'} gap={2}>
                        <Controller 
                            name="EmailAddress"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    error={errors.EmailAddress != undefined}
                                    fieldLabel='Email address'
                                />
                            )}
                        />
                        <Controller 
                            name="Website"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    error={errors.Website != undefined}
                                    fieldLabel='Website (optional)'
                                    placeholder='Link to website'
                                />
                            )}
                        />
                    </Grid>
                    <Grid display={'flex'} flexDirection={'row'} gap={2}>
                        <Controller 
                            name="Industry"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziIndustries 
                                    {...field}
                                    showLabel
                                    error={errors.Industry != undefined}
                                    placeholder='Industry'
                                />
                            )}
                        />
                        <Controller 
                            name="Country"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziCountries 
                                    {...field}
                                    error={errors.Country != undefined}
                                    showLabel
                                />
                            )}
                        />
                    </Grid>

                    <Grid display={'flex'} flexDirection={'row'} gap={2}>
                        <Controller 
                            name="Description"
                            control={control}
                            rules={{required: true}}
                            render={({ field }) =>( 
                                <KaziTextFieldWithLabel 
                                    {...field}
                                    multiline
                                    maxRows={5}
                                    error={errors.Description != undefined}
                                    fieldLabel='Description of services'
                                />
                            )}
                        />
                    </Grid>
                </>
            }
            actions={[
                <KaziButton
                    onClick={handleSubmit(onSubmit)}
                    variant='contained'
                    disabled={!isValid}
                    text='Submit'
                    loading={{
                        isLoading: requestLoading,
                        loadingText: 'Submitting...'
                    }}
                />
            ]}
        />
    )
}